package fyi.moko.hotpl.data.service

import fyi.moko.hotpl.domain.service.DestinationHistoryStore
import fyi.moko.hotpl.domain.service.withRecordedDestination
import kotlinx.browser.localStorage
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.serialization.json.Json

/**
 * `localStorage`-backed [DestinationHistoryStore] — the JS analog of
 * [AndroidDestinationHistoryStore]/[IosDestinationHistoryStore]. Reads/writes are
 * defensive (private browsing or a full quota can make `localStorage` throw), matching
 * `JsPlaceStore`'s pattern: a failure just starts/leaves the list empty rather than
 * crashing the Directory tab.
 */
class JsDestinationHistoryStore : DestinationHistoryStore {
    private val json = Json { ignoreUnknownKeys = true }
    private val _recentDestinations = MutableStateFlow(loadFromStorage())
    override val recentDestinations = _recentDestinations.asStateFlow()

    override suspend fun record(destination: String) {
        val trimmed = destination.trim()
        if (trimmed.isBlank()) return
        val updated = _recentDestinations.value.withRecordedDestination(trimmed)
        _recentDestinations.value = updated
        try {
            localStorage.setItem(KEY, json.encodeToString(updated))
        } catch (e: Throwable) {
            console.error("Failed to persist recent destinations to localStorage", e)
        }
    }

    private fun loadFromStorage(): List<String> {
        val raw = try {
            localStorage.getItem(KEY)
        } catch (e: Throwable) {
            null
        } ?: return emptyList()
        return try {
            json.decodeFromString<List<String>>(raw)
        } catch (e: Throwable) {
            console.error("Failed to read persisted recent destinations; starting empty", e)
            emptyList()
        }
    }

    private companion object {
        const val KEY = "hotpl.directory.recent_destinations.v1"
    }
}
